/**
 * Start Here — a guided checklist of the student's next steps (iter 156).
 *
 * Greeting + an optional orientation video on top, then the Microsoft-admin-centre style table
 * (Action · Status · Description) driven by `/api/next-steps/me`; copy is admin-editable.
 * Marks `start_here_seen` once so the post-onboarding redirect runs only the first time.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { fill, greeting, stepVars, useNextSteps } from '../lib/nextSteps';
import StepsChecklist from '../components/nextsteps/StepsChecklist';
import ClassReminderStrip from '../components/nextsteps/ClassReminderStrip';

// Normalise share URLs from Loom / YouTube / Vimeo to their /embed/ form.
function normalizeEmbedUrl(raw) {
  if (!raw) return '';
  const url = String(raw).trim();
  const loom = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/(?:^|\/\/)(?:www\.)?vimeo\.com\/(?!video\/)(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

export default function StartHere() {
  const [config, setConfig] = useState(null);
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const ns = useNextSteps();

  useEffect(() => { api.get('/api/start-here/config').then((r) => setConfig(r.data || {})).catch(() => setConfig({})); }, []);
  useEffect(() => {
    if (user && !user.start_here_seen) api.post('/api/start-here/mark-seen').then(() => refreshUser?.()).catch(() => {});
  }, [user, refreshUser]);

  const videoEmbed = normalizeEmbedUrl(config?.video_url);
  const first = (user?.name || '').trim().split(' ')[0];
  const vars = ns.data ? stepVars(ns.data) : {};
  const done = ns.data?.steps?.length > 0 && ns.data.progress.done === ns.data.progress.total;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl" data-testid="start-here-page">
        <div>
          <h1 className="font-[Manrope] text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="start-here-greeting">{greeting()}{first ? `, ${first}` : ''}</h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="start-here-subtitle">
            {ns.data ? fill(ns.data.track_title, vars) : (config?.subtitle || 'Welcome to CELPIPMax!')}
            {ns.data && !done && <> — {ns.data.progress.total - ns.data.progress.done} step{ns.data.progress.total - ns.data.progress.done === 1 ? '' : 's'} to go.</>}
          </p>
        </div>

        {ns.data?.class && <ClassReminderStrip cls={ns.data.class} texts={ns.data.texts} vars={vars} onChanged={ns.refresh} />}

        {ns.loading && !ns.data && <div className="text-sm text-slate-400" data-testid="start-here-loading">Loading your steps…</div>}
        {ns.data && ns.data.steps.length > 0 && <StepsChecklist data={ns.data} onChanged={ns.refresh} />}
        {ns.data && ns.data.steps.length === 0 && <p className="text-sm text-slate-500" data-testid="start-here-empty">Your checklist is being prepared — head to the dashboard for now.</p>}

        {videoEmbed && (
          <div className="bg-white rounded-[28px] border border-slate-200 overflow-hidden shadow-sm" data-testid="start-here-video-card">
            <p className="px-5 pt-4 pb-3 text-sm font-semibold text-slate-800">{config?.subtitle || 'Watch: how to make the most of CELPIPMax'}</p>
            <div className="relative w-full bg-slate-900" style={{ paddingTop: '56.25%' }}>
              <iframe src={videoEmbed} title="Start Here video" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; clipboard-write" allowFullScreen className="absolute inset-0 w-full h-full border-0" data-testid="start-here-video-iframe" />
            </div>
          </div>
        )}

        <div className="pt-2">
          <button onClick={() => navigate('/dashboard')} className="text-sm text-slate-400 hover:text-slate-700 transition-colors" data-testid="start-here-skip-btn">
            {done ? 'Go to Dashboard →' : 'Skip for now → Go to Dashboard'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
